import { type FC, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useChildrenInfiniteQuery } from 'api/decomposition-tree/hooks/useChildrenInfiniteQuery';
import { useStickyTopNodes } from 'api/decomposition-tree/hooks/useStickyTopNodes';
import { treeQueryKeys } from 'api/decomposition-tree/utils/treeQueryKeys';

import type { ChildrenSnapshot } from '../types';
import { mergeStickyWithPaginatedAsTreeItems } from '../utils/mergeNodes';
import { errorId } from '../utils/treeId';

type ChildrenSubscriberProps = {
  parentTreeId: string;
  parentDataNodeId: string;
  projectId: string;
  templateNodeId: string;
  proposalId?: string;
  titlePlaceholder?: string;
  onSnapshot: (parentTreeId: string, snapshot: ChildrenSnapshot) => void;
};

/**
 * Headless component: mounts a `useInfiniteQuery` for one parent and
 * relays its state upward.
 *
 * Why a component (not just a hook in the parent)?
 *  - We need one independent `useInfiniteQuery` *per opened parent*, so a
 *    plain hook call in the orchestrator won't work (Rules of Hooks
 *    forbid loops over a dynamic list of queries with conditional
 *    enablement).
 *  - Rendering one subscriber per opened parent gives us N independent
 *    subscriptions cleanly. React-query keeps the cache shared across them.
 *  - When the parent collapses, the subscriber unmounts. Successful fetches
 *    stay cached so reopening is instantaneous; failed fetches are dropped so
 *    re-expanding triggers a fresh request.
 */
export const ChildrenSubscriber: FC<ChildrenSubscriberProps> = ({
  parentTreeId,
  parentDataNodeId,
  projectId,
  templateNodeId,
  proposalId,
  titlePlaceholder,
  onSnapshot
}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => treeQueryKeys.children({ projectId, templateNodeId, proposalId }, parentDataNodeId),
    [projectId, templateNodeId, parentDataNodeId, proposalId]
  );

  const { data, hasNextPage, isFetchingNextPage, isPending, isFetching, isSuccess, isError, fetchNextPage } =
    useChildrenInfiniteQuery(projectId, templateNodeId, parentDataNodeId, proposalId);

  const stickyChildren = useStickyTopNodes(projectId, templateNodeId, proposalId, parentDataNodeId);

  // Stable wrapper so the snapshot's callback identity doesn't change on every
  // run — otherwise the equality guard in `handleSnapshot` never matches.
  const requestNextPage = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    return () => {
      if (queryClient.getQueryState(queryKey)?.status === 'error') {
        queryClient.removeQueries({ queryKey });
      }
    };
  }, [queryClient, queryKey]);

  // useLayoutEffect (not useEffect) so cached children resolve into the tree in
  // the same paint as the expand, avoiding a one-frame flash of the placeholder.
  useLayoutEffect(() => {
    if (isError) {
      onSnapshot(parentTreeId, {
        items: [{ id: errorId(parentTreeId), dataNodeId: '', title: '', autoId: '', label: 'Failed to load' }],
        hasMore: false,
        isFetching: isFetching || isFetchingNextPage,
        isError: true,
        fetchNextPage: requestNextPage
      });
      return;
    }

    if (!isSuccess || !data) return;

    onSnapshot(parentTreeId, {
      items: mergeStickyWithPaginatedAsTreeItems(stickyChildren, data.pages, parentTreeId, titlePlaceholder),
      hasMore: !!hasNextPage,
      isFetching: isPending || isFetching || isFetchingNextPage,
      isError: false,
      fetchNextPage: requestNextPage
    });
  }, [
    data,
    requestNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    isSuccess,
    onSnapshot,
    parentTreeId,
    stickyChildren,
    titlePlaceholder
  ]);

  return null;
};
